import type { components } from '@connected/api-client/api.generated';

import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export function useInverters({
    electricalProjectVersionId,
}: {
    electricalProjectVersionId: string;
}) {
    if (!electricalProjectVersionId) {
        throw new Error('electricalProjectVersionId is required');
    }

    const inverters = useSuspenseQuery(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
        electricalProjectVersionId,
    }));

    return {
        data: {
            inverters: inverters?.data?.Inverters as components['schemas']['ListElectricalProjectPVInverterQueryResultInverter'][],
            count: inverters?.data?.Inverters?.length || 0,
        },
        isLoading: inverters.isLoading,
        isError: inverters.isError,
        error: inverters.error,
    };
}

export function useInverter({
    electricalProjectVersionId,
    pvInverterId,
}: {
    electricalProjectVersionId: string;
    pvInverterId: string;
}) {
    if (!electricalProjectVersionId) {
        throw new Error('electricalProjectVersionId is required');
    }

    if (!pvInverterId) {
        throw new Error('pvInverterId is required');
    }

    const inverters = useSuspenseQuery(elektraAppQueryOptions.project.inspection.pvinverter.PVInverterListOptions({
        electricalProjectVersionId,
    }));

    const inverter = useMemo(() => inverters.data?.Inverters?.find(
        inverter => inverter.ElectricalProjectPVInverterId === pvInverterId,
    ) || null, [inverters.data?.Inverters, pvInverterId]);

    return {
        data: {
            inverter: inverter as components['schemas']['ListElectricalProjectPVInverterQueryResultInverter'] | null,
        },
        isLoading: inverters.isLoading,
        isError: inverters.isError,
        error: inverters.error,
    };
}
